import React, { useState } from 'react';
import { SearchIcon, FilterIcon, PlusIcon, EditIcon, TrashIcon, FileIcon, CheckCircleIcon, ClockIcon, AlertTriangleIcon, XIcon, CalendarIcon, DollarSignIcon, DownloadIcon } from 'lucide-react';
export function ContractList({
  locale,
  onSelectContract
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    vendor: '',
    dateRange: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  // Sample contract data
  const contracts = [{
    id: 'CTR-2023-001',
    name: 'Office Lease Agreement',
    vendor: 'Skyline Properties Inc.',
    status: 'Active',
    startDate: '2023-01-15',
    endDate: '2026-01-14',
    value: 240000,
    type: 'Lease'
  }, {
    id: 'CTR-2023-002',
    name: 'IT Support Services',
    vendor: 'TechPro Solutions',
    status: 'Active',
    startDate: '2023-02-01',
    endDate: '2024-01-31',
    value: 120000,
    type: 'Service'
  }, {
    id: 'CTR-2023-003',
    name: 'Software License Agreement',
    vendor: 'Enterprise Software Inc.',
    status: 'Pending Review',
    startDate: '2023-03-15',
    endDate: '2024-03-14',
    value: 85000,
    type: 'License'
  }, {
    id: 'CTR-2023-004',
    name: 'Marketing Services',
    vendor: 'Creative Marketing Group',
    status: 'Draft',
    startDate: '',
    endDate: '',
    value: 50000,
    type: 'Service'
  }, {
    id: 'CTR-2023-005',
    name: 'Equipment Maintenance',
    vendor: 'Industrial Maintenance Co.',
    status: 'Expiring Soon',
    startDate: '2022-06-01',
    endDate: '2023-12-31',
    value: 35000,
    type: 'Service'
  }, {
    id: 'CTR-2023-006',
    name: 'Consulting Services',
    vendor: 'Business Consultants Ltd.',
    status: 'Active',
    startDate: '2023-04-01',
    endDate: '2024-03-31',
    value: 95000,
    type: 'Service'
  }, {
    id: 'CTR-2023-007',
    name: 'Office Supplies',
    vendor: 'Office Depot',
    status: 'Active',
    startDate: '2023-01-01',
    endDate: '2023-12-31',
    value: 25000,
    type: 'Purchase'
  }, {
    id: 'CTR-2023-008',
    name: 'Security Services',
    vendor: 'SecureGuard Inc.',
    status: 'Active',
    startDate: '2023-02-15',
    endDate: '2024-02-14',
    value: 75000,
    type: 'Service'
  }, {
    id: 'CTR-2023-009',
    name: 'Catering Services',
    vendor: 'Gourmet Catering Co.',
    status: 'Expired',
    startDate: '2022-01-01',
    endDate: '2022-12-31',
    value: 30000,
    type: 'Service'
  }, {
    id: 'CTR-2023-010',
    name: 'Cloud Infrastructure',
    vendor: 'AWS',
    status: 'Active',
    startDate: '2023-03-01',
    endDate: '2024-02-29',
    value: 180000,
    type: 'Service'
  }];
  // Format currency
  const formatCurrency = value => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  // Get status badge color
  const getStatusColor = status => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Pending Review':
        return 'bg-yellow-100 text-yellow-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Expiring Soon':
        return 'bg-orange-100 text-orange-800';
      case 'Expired':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status icon
  const getStatusIcon = status => {
    switch (status) {
      case 'Active':
        return CheckCircleIcon;
      case 'Pending Review':
        return ClockIcon;
      case 'Draft':
        return FileIcon;
      case 'Expiring Soon':
        return AlertTriangleIcon;
      case 'Expired':
        return XIcon;
      default:
        return FileIcon;
    }
  };
  // Filter contracts based on search and filters
  const filteredContracts = contracts.filter(contract => {
    // Search term filter
    if (searchTerm && !contract.name.toLowerCase().includes(searchTerm.toLowerCase()) && !contract.id.toLowerCase().includes(searchTerm.toLowerCase()) && !contract.vendor.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    // Status filter
    if (filters.status && contract.status !== filters.status) {
      return false;
    }
    // Type filter
    if (filters.type && contract.type !== filters.type) {
      return false;
    }
    // Vendor filter
    if (filters.vendor && contract.vendor !== filters.vendor) {
      return false;
    }
    return true;
  });
  // Get unique values for filters
  const uniqueStatuses = [...new Set(contracts.map(c => c.status))];
  const uniqueTypes = [...new Set(contracts.map(c => c.type))];
  const uniqueVendors = [...new Set(contracts.map(c => c.vendor))];
  const handleFilterChange = (name, value) => {
    setFilters({
      ...filters,
      [name]: value
    });
  };
  const clearFilters = () => {
    setFilters({
      status: '',
      type: '',
      vendor: '',
      dateRange: ''
    });
    setSearchTerm('');
  };
  return <div data-id="element-2378">
      <div className="mb-8 flex justify-between items-center" data-id="element-2379">
        <div data-id="element-2380">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-2381">Contract List</h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2382">
            View and manage all contracts
          </p>
        </div>
        <div className="flex space-x-2" data-id="element-2383">
          <button onClick={() => onSelectContract(null)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-2384">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-2385" />
            Add Contract
          </button>
        </div>
      </div>
      {/* Filters */}
      <div className="bg-white shadow-sm rounded-lg p-4 mb-6" data-id="element-2386">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-2387">
          <div className="flex-1 max-w-md" data-id="element-2388">
            <div className="relative rounded-md shadow-sm" data-id="element-2389">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2390">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2391" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search contracts..." data-id="element-2392" />
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-2393">
            <button onClick={() => setShowFilters(!showFilters)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none" data-id="element-2394">
              <FilterIcon className="h-4 w-4 mr-2" data-id="element-2395" />
              Filters
              {Object.values(filters).some(f => f !== '') && <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-2396">
                  {Object.values(filters).filter(f => f !== '').length}
                </span>}
            </button>
            <button onClick={() => {}} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none" data-id="element-2397">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-2398" />
              Export
            </button>
          </div>
        </div>
        {/* Filter chips */}
        {Object.values(filters).some(f => f !== '') && <div className="mt-4 flex flex-wrap gap-2" data-id="element-2399">
            {filters.status && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800" data-id="element-2400">
                Status: {filters.status}
                <button type="button" onClick={() => handleFilterChange('status', '')} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none" data-id="element-2401">
                  <XIcon className="h-3 w-3" data-id="element-2402" />
                </button>
              </span>}
            {filters.type && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-purple-100 text-purple-800" data-id="element-2403">
                Type: {filters.type}
                <button type="button" onClick={() => handleFilterChange('type', '')} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-purple-400 hover:bg-purple-200 hover:text-purple-500 focus:outline-none" data-id="element-2404">
                  <XIcon className="h-3 w-3" data-id="element-2405" />
                </button>
              </span>}
            {filters.vendor && <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-2406">
                Vendor: {filters.vendor}
                <button type="button" onClick={() => handleFilterChange('vendor', '')} className="ml-1.5 inline-flex items-center justify-center h-4 w-4 rounded-full text-green-400 hover:bg-green-200 hover:text-green-500 focus:outline-none" data-id="element-2407">
                  <XIcon className="h-3 w-3" data-id="element-2408" />
                </button>
              </span>}
            <button type="button" onClick={clearFilters} className="inline-flex items-center px-2 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none" data-id="element-2409">
              Clear All
            </button>
          </div>}
        {/* Expanded filters */}
        {showFilters && <div className="mt-4 grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-6" data-id="element-2410">
            <div className="sm:col-span-2" data-id="element-2411">
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700" data-id="element-2412">
                Status
              </label>
              <select id="status-filter" value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-2413">
                <option value="" data-id="element-2414">All Statuses</option>
                {uniqueStatuses.map(status => <option key={status} value={status} data-id="element-2415">
                    {status}
                  </option>)}
              </select>
            </div>
            <div className="sm:col-span-2" data-id="element-2416">
              <label htmlFor="type-filter" className="block text-sm font-medium text-gray-700" data-id="element-2417">
                Contract Type
              </label>
              <select id="type-filter" value={filters.type} onChange={e => handleFilterChange('type', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-2418">
                <option value="" data-id="element-2419">All Types</option>
                {uniqueTypes.map(type => <option key={type} value={type} data-id="element-2420">
                    {type}
                  </option>)}
              </select>
            </div>
            <div className="sm:col-span-2" data-id="element-2421">
              <label htmlFor="vendor-filter" className="block text-sm font-medium text-gray-700" data-id="element-2422">
                Vendor
              </label>
              <select id="vendor-filter" value={filters.vendor} onChange={e => handleFilterChange('vendor', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-2423">
                <option value="" data-id="element-2424">All Vendors</option>
                {uniqueVendors.map(vendor => <option key={vendor} value={vendor} data-id="element-2425">
                    {vendor}
                  </option>)}
              </select>
            </div>
          </div>}
      </div>
      {/* Contracts Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2426">
        <div className="overflow-x-auto" data-id="element-2427">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-2428">
            <thead className="bg-gray-50" data-id="element-2429">
              <tr data-id="element-2430">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2431">
                  Contract
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2432">
                  Vendor
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2433">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2434">
                  Dates
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2435">
                  Value
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2436">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2437">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2438">
              {filteredContracts.length === 0 ? <tr data-id="element-2439">
                  <td colSpan={7} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-2440">
                    No contracts found matching your criteria.
                  </td>
                </tr> : filteredContracts.map(contract => {
              const StatusIcon = getStatusIcon(contract.status);
              return <tr key={contract.id} className="hover:bg-gray-50" data-id="element-2441">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2442">
                        <div className="flex items-center" data-id="element-2443">
                          <div data-id="element-2444">
                            <div className="text-sm font-medium text-indigo-600" data-id="element-2445">
                              {contract.name}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-2446">
                              {contract.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2447">
                        {contract.vendor}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2448">
                        {contract.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2449">
                        {contract.startDate && contract.endDate ? <div className="flex items-center" data-id="element-2450">
                            <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-2451" />
                            <span data-id="element-2452">
                              {new Date(contract.startDate).toLocaleDateString(locale)}{' '}
                              -{' '}
                              {new Date(contract.endDate).toLocaleDateString(locale)}
                            </span>
                          </div> : <span className="text-gray-400" data-id="element-2453">No dates set</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-2454">
                        <div className="flex items-center" data-id="element-2455">
                          <DollarSignIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-2456" />
                          {formatCurrency(contract.value)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2457">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`} data-id="element-2458">
                          <StatusIcon className="h-3 w-3 mr-1" data-id="element-2459" />
                          {contract.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2460">
                        <div className="flex justify-end space-x-2" data-id="element-2461">
                          <button onClick={() => onSelectContract(contract)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-2462">
                            <EditIcon className="h-4 w-4" data-id="element-2463" />
                          </button>
                          <button className="text-red-600 hover:text-red-900" title="Delete" data-id="element-2464">
                            <TrashIcon className="h-4 w-4" data-id="element-2465" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}